import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faEye, faXmark } from "@fortawesome/free-solid-svg-icons";
import { ProviderRecibeApi, ProviderRecibeDataType } from "@/api/ProviderRecibeApi";
import { formatMoneyValue } from "@utils/Currency";
import { getPaymentMethodLabel } from "@utils/Utils";
import SendDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendDocumentToEmailOffCanvas";
import { useAppSelector } from "@/store/hooks";
import moment from "moment";
import LoadingScreen from "@components/content/LoadingScreen";
import { showAlert } from "@utils/Alert";
import { ProviderInvoiceApi } from "@/api/ProviderInvoiceApi";
import { BuyInvoiceApi } from "@/api/BuyInvoiceApi";
import { ProviderInvoicePaymentApi } from "@/api/ProviderInvoicePaymentApi";
import { BuyInvoicePaymentApi } from "@/api/BuyInvoicePaymentApi";
import { INVOICE_PAYMENT_CANCELLED_STATUS } from "@utils/Constants";

const ProviderRecibeIndex = () => {
  const navigate = useNavigate();
  const modelRef = useRef<ModelIndexHandle>(null);
  const suscriptionData = useAppSelector(state => state.suscription.suscription)
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
  }, []);

  const cancelProviderRecibe = async (cancelProviderRecibe: ProviderRecibeDataType) => {
    try {
      setIsLoading(true)
      const updatePromises = []
      const providerRecibeId = `${cancelProviderRecibe.id}`
      const invoices = await ProviderInvoiceApi.getInvoiceByRecibeId(providerRecibeId)
      const { items: buyInvoices } = await BuyInvoiceApi.fetchAllByRecibe(providerRecibeId)
      // Abonos parciales generados por este recibo: se marcan como anulados para que el
      // saldo de la factura que quedo pendiente vuelva al valor original.
      const { items: providerInvoicePayments } = await ProviderInvoicePaymentApi.fetchAllByRecibe(providerRecibeId)
      const { items: buyInvoicePayments } = await BuyInvoicePaymentApi.fetchAllByRecibe(providerRecibeId)
      updatePromises.push(ProviderRecibeApi.update(providerRecibeId, { anulado: true }))

      const invoicesPromises = invoices.map(s => ProviderInvoiceApi.update(`${s.id}`,
        {
          pagada: false,
          status: "pendiente",
          providerId: `${s.providerId}`,
          providerRecibeId: null,
          fecha: s.fecha,
          user: s.user
        },
        true))

      const buyInvoicesPromises = buyInvoices.map(s => BuyInvoiceApi.update(`${s.id}`,
        {
          pagada: false,
          status: "pendiente",
          providerId: `${s.providerId}`,
          providerRecibeId: null,
          buyInvoiceType: "Buy",
          fecha: s.fecha,
          user: s.user
        },
        true))

      const paymentsPromises = [
        ...providerInvoicePayments.map(p => ProviderInvoicePaymentApi.update(`${p.id}`, { estado: INVOICE_PAYMENT_CANCELLED_STATUS })),
        ...buyInvoicePayments.map(p => BuyInvoicePaymentApi.update(`${p.id}`, { estado: INVOICE_PAYMENT_CANCELLED_STATUS })),
      ]

      updatePromises.push(...invoicesPromises, ...buyInvoicesPromises, ...paymentsPromises)

      const promisesResult = await Promise.all(updatePromises);
      showAlert("success", "El recibo ha sido cancelado correctamente")
      modelRef.current?.refetch()

    } catch (err) {
      if (err instanceof Error) {
        showAlert("danger", "No fue posible anular recibo")
      }
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <PageContainer
      title="Lista de Recibos de Provedor"
      backUrl="/user/dashboard-accounting"
      primaryActions={[
        {
          onClick: () => {
            navigate('/user/providers/payInvoices')
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
      secondaryActions={[
        {
          onClick: () => {
            navigate('/user/provider-recibes/repair')
          },
          actionTitle: "Reparar recibos incompletos",
          color: "warning"
        }
      ]}
    >
      {!!suscriptionData?.id && (
        <ModelIndex<ProviderRecibeDataType>
          ref={modelRef}
          // meiliConfig={{
          //   index: "providerRecibes",
          //   sort: sortSetup
          // }}
          fetchData={(...args) => ProviderRecibeApi.fetchAll(...args)}
          filterProps={[
            {
              key: "numero_recibo",
              filterType: "eq",
              type: "number"
            }
          ]}
          primaryProperties={[
            {
              key: "numero_recibo",
              highlighted: true,
              optionalHighlighted: (val: any, data: ProviderRecibeDataType) => { return data.anulado || false },
              highlightedColors: {
                "true": {
                  "bg": "#dc3545",
                  "text": "#fff"
                },
                "false": {
                  "bg": "#337ab7",
                  "text": "#fff"
                }
              },
            },
            {
              key: (data) => {
                if ("providerNombre" in data) {
                  return (data as any).providerNombre
                }

                return data.provider && data.provider?.nombre || ""
              },
              alignment: "Start"
            },
          ]}
          secondaryProperties={[
            {
              key: (data) => {
                const date = moment(data.created, "YYYY-MM-DD")
                return date.format("DD-MM-YYYY")
              },
              title: "Fecha",
            },
            {
              key: (data) => getPaymentMethodLabel(data.forma_pago),
              title: "Forma Pago",
            },
            {
              key: (data) => {
                if ("currency_id" in data) {
                  return formatMoneyValue(data.abono, (data as any).currency_id)
                }
                return formatMoneyValue(data.abono, data.currency.currency_id)
              },
              title: "Total",
              highlighted: true
            }
          ]}
          listActions={[
            {
              isVisible: (data) => !data.anulado,
              onClick: (id, item) => cancelProviderRecibe(item),
              actionTitle: "Anular Recibo",
              iconProps: { icon: faXmark },
              needConfirm: true,
              confirmProps: {
                confirmMessage: "Esta seguro que desea anular este Recibo? ",
              }
            },
            {
              href: "/user/provider-recibes/view/{:id}",
              actionTitle: "Ver Recibo",
              target: "_blank",
              iconProps: { icon: faEye }
            }
          ]}
        >
        </ModelIndex>
      )}

      <SendDocumentToEmailOffCanvas
        title="Envio de Recibo a Email" />
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default ProviderRecibeIndex;
